import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { 
  Upload, 
  X, 
  Plus, 
  Save, 
  Trash2,
  ArrowLeft,
  BookOpen,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const EditSeries = () => {
  const { id } = useParams();
  const [series, setSeries] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    cover_image: '',
    categories: []
  });
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  
  const { user, token, API } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSeries();
  }, [id]);

  const fetchSeries = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/series/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const seriesData = response.data;
      
      // Check if user owns this series
      if (seriesData.author_id !== user.id) {
        toast.error('You can only edit your own series');
        navigate('/dashboard');
        return;
      }
      
      setSeries(seriesData);
      setFormData({
        name: seriesData.name || '',
        description: seriesData.description || '',
        cover_image: seriesData.cover_image || '',
        categories: [...seriesData.categories]
      });
      
    } catch (error) {
      console.error('Error fetching series:', error);
      toast.error('Failed to load series');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          cover_image: event.target.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeCoverImage = () => {
    setFormData(prev => ({
      ...prev,
      cover_image: ''
    }));
  };

  const addCategory = () => {
    if (newCategory.trim() && !formData.categories.includes(newCategory.trim())) {
      setFormData(prev => ({
        ...prev,
        categories: [...prev.categories, newCategory.trim()]
      }));
      setNewCategory('');
    }
  };

  const removeCategory = (category) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter(c => c !== category)
    }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Series name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await axios.put(`${API}/series/${id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Series updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error updating series:', error);
      setError(error.response?.data?.detail || 'Failed to update series');
      toast.error('Failed to update series');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${series.name}" and all its episodes? This action cannot be undone.`)) {
      return;
    }

    const confirmText = prompt(
      `This will delete the entire series and all ${series.episodes_count} episodes. Type "DELETE" to confirm:`
    );

    if (confirmText !== 'DELETE') {
      toast.error('Series deletion cancelled');
      return;
    }

    setSaving(true);

    try {
      await axios.delete(`${API}/series/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Series deleted successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting series:', error);
      toast.error('Failed to delete series');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <div className="text-xl font-medium text-gray-600">Loading series...</div>
      </div>
    );
  }

  if (!series) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50 flex items-center justify-center">
        <Alert className="max-w-md">
          <AlertDescription>Series not found or you don't have permission to edit it.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-orange-50 to-yellow-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 animate-fade-in-up">
            <div>
              <h1 className="text-4xl font-bold gradient-text mb-4">Edit Series</h1>
              <p className="text-gray-600">Update your series information</p>
            </div>
            
            <div className="flex space-x-2">
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button 
                onClick={handleDelete}
                variant="destructive"
                disabled={saving}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete Series
              </Button>
            </div>
          </div>

          <Card className="glass-card animate-slide-in-right">
            <CardHeader>
              <CardTitle className="flex items-center">
                <BookOpen className="w-5 h-5 mr-2" />
                {series.name} ({series.episodes_count} episodes)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {/* Series Name */}
              <div className="space-y-2">
                <Label htmlFor="name">Series Name *</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter series name"
                  className="form-input"
                />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe your series story and theme..."
                  className="form-input resize-none"
                  rows={4}
                />
              </div>

              {/* Cover Image */}
              <div className="space-y-4">
                <Label>Cover Image (Optional)</Label>
                
                {!formData.cover_image ? (
                  <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-purple-400 transition-colors">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="cover-upload"
                    />
                    <label
                      htmlFor="cover-upload"
                      className="cursor-pointer flex flex-col items-center space-y-2"
                    >
                      <Upload className="w-10 h-10 text-gray-400" />
                      <span className="text-gray-600">Click to upload cover image</span>
                      <span className="text-sm text-gray-500">PNG, JPG, GIF up to 10MB</span>
                    </label>
                  </div>
                ) : (
                  <div className="relative group">
                    <img
                      src={formData.cover_image}
                      alt="Series cover"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                    <button
                      type="button"
                      onClick={removeCoverImage}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Categories */}
              <div className="space-y-4">
                <Label>Categories</Label>
                
                <div className="flex flex-wrap gap-2 mb-3">
                  {formData.categories.map((category) => (
                    <Badge key={category} variant="secondary" className="category-tag">
                      {category}
                      <button
                        type="button"
                        onClick={() => removeCategory(category)}
                        className="ml-2 text-gray-500 hover:text-gray-700"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>

                <div className="flex gap-2">
                  <Input
                    value={newCategory}
                    onChange={(e) => setNewCategory(e.target.value)}
                    placeholder="Add category (e.g., Action, Comedy, Drama)"
                    className="form-input flex-1"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCategory())}
                  />
                  <Button type="button" onClick={addCategory} variant="outline">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Episode Count Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-blue-800">
                  <BookOpen className="w-5 h-5" />
                  <span className="font-medium">Series Statistics</span>
                </div>
                <div className="mt-2 text-sm text-blue-700">
                  This series currently has {series.episodes_count} episode{series.episodes_count !== 1 ? 's' : ''}. 
                  You can manage individual episodes from the main dashboard.
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-6">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="btn-primary"
                  size="lg"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EditSeries;